<script>
import { onMounted } from "vue";
import PrincipalHeader from "@/views/pages/dashboard/principal/components/Principal-header.vue";
import PrincipalSidebar from "@/views/pages/dashboard/principal/components/Principal-sidebar.vue";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import { useSharedStore } from "@/stores/shared-store";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store";
import { useAuthStore } from "@/stores/auth-store";

export default {
  components: {
    PrincipalHeader,
    PrincipalSidebar,
    BaseLoading,
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10,
      pageWindowSize: 6,
      searchKeyword: "",
    };
  },
  setup() {
    const sharedStore = useSharedStore();
    const authStore = useAuthStore();
    const courseFetchStore = useCourseFetchStore();

    onMounted(() => {
      courseFetchStore.fetchCourseList();
      authStore.authUser();
    });
    return {
      sharedStore,
      courseFetchStore,
      authStore,
    };
  },
  computed: {
    filteredCourses() {
      const courses = this.courseFetchStore.unapprovedCourses || [];
      const keyword = this.searchKeyword.trim().toLowerCase();
      if (!keyword) return courses;

      return courses.filter((course) => {
        const title = course.title?.toLowerCase() || "";
        const instructor = this.authStore
          .userName(course.instructor)
          ?.toLowerCase();
        return title.includes(keyword) || instructor?.includes(keyword);
      });
    },
    totalPages() {
      return Math.max(
        1,
        Math.ceil(this.filteredCourses.length / this.itemsPerPage)
      );
    },
    paginatedCourses() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredCourses.slice(start, end);
    },
    visiblePages() {
      const total = this.totalPages;
      const windowSize = this.pageWindowSize;
      if (total <= windowSize) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const chunkStart =
        Math.floor((this.currentPage - 1) / windowSize) * windowSize + 1;
      const chunkEnd = Math.min(chunkStart + windowSize - 1, total);
      return Array.from(
        { length: chunkEnd - chunkStart + 1 },
        (_, i) => chunkStart + i
      );
    },
  },
  watch: {
    filteredCourses() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    onSearch() {
      this.currentPage = 1;
    },
  },
};
</script>
<template>
  <layouts-header></layouts-header>
  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Dashboard</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Dashboard
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->
  <div class="content">
    <div class="container">
      <PrincipalHeader></PrincipalHeader>
      <div class="row">
        <PrincipalSidebar></PrincipalSidebar>
        <div class="col-lg-9">
          <div class="col-md-12 text-center" v-if="sharedStore.loading">
            <BaseLoading :dialog="false"></BaseLoading>
          </div>
          <div v-else>
            <div
              class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
            >
              <h5 class="mb-0 fw-bold">Approve Course Request</h5>
              <div class="input-icon" style="min-width: 220px">
                <span class="input-icon-addon">
                  <i class="isax isax-search-normal-14"></i>
                </span>
                <input
                  type="text"
                  class="form-control form-control-md"
                  placeholder="Search course or instructor"
                  v-model="searchKeyword"
                  @input="onSearch"
                />
              </div>
            </div>
            <div class="table-responsive custom-table">
              <table class="table">
                <thead class="thead-light">
                  <tr>
                    <th>Courses</th>
                    <th>Instructor</th>
                    <th>Status</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredCourses.length === 0">
                    <td colspan="4" class="text-center py-5">
                      <div class="d-inline-flex icon-box bg-soft-success mb-3">
                        <img src="@/assets/img/icon/bookmark.svg" alt="" />
                      </div>
                      <h6 class="mb-1">No Pending Course Requests</h6>
                      <p class="mb-0 fs-14 text-gray-5">
                        {{
                          searchKeyword
                            ? "No courses match your search."
                            : "Courses waiting for approval will appear here."
                        }}
                      </p>
                    </td>
                  </tr>
                  <tr
                    v-for="(item, index) in paginatedCourses"
                    :key="item.course_id || index"
                  >
                    <td>
                      <div class="course-title d-flex align-items-center">
                        <router-link
                          :to="{
                            name: 'CourseDetail',
                            params: { course_id: item.course_id },
                          }"
                          class="avatar avatar-xl flex-shrink-0 me-2"
                          ><img
                            :src="`${authStore.Url}/media/${item.thumbnail}`"
                            alt="Img"
                        /></router-link>
                        <div>
                          <p class="fw-medium">
                            <router-link
                              :to="{
                                name: 'CourseDetail',
                                params: { course_id: item.course_id },
                              }"
                              >{{ item.title }}</router-link
                            >
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>{{ authStore.userName(item.instructor) }}</td>
                    <td>
                      <span
                        v-if="item.status === 'Active'"
                        class="badge badge-sm bg-success d-inline-flex align-items-center me-1"
                        ><i class="fa-solid fa-circle fs-5 me-1"></i
                        >{{ item.status }}</span
                      >
                      <span
                        v-if="item.status === 'New'"
                        class="badge badge-sm bg-warning d-inline-flex align-items-center me-1"
                        ><i class="fa-solid fa-circle fs-5 me-1"></i
                        >{{ item.status }}</span
                      >
                      <span
                        v-if="item.status === 'Need Approval Edit'"
                        class="badge badge-sm bg-info d-inline-flex align-items-center me-1"
                        ><i class="fa-solid fa-circle fs-5 me-1"></i
                        >Need Approval Edit</span
                      >
                      <span
                        v-if="item.status === 'Disabled'"
                        class="badge badge-sm bg-danger d-inline-flex align-items-center me-1"
                        ><i class="fa-solid fa-circle fs-5 me-1"></i
                        >{{ item.status }}</span
                      >
                    </td>
                    <td>{{ sharedStore.formatTanggal(item.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              class="row align-items-center mt-4"
              v-if="filteredCourses.length > 0"
            >
              <div class="col-md-2">
                <p class="fs-14 fw-500 text-center text-md-start">
                  Page {{ currentPage }} of {{ totalPages }}
                </p>
              </div>
              <div class="col-md-10">
                <ul
                  class="pagination lms-page justify-content-center justify-content-md-end mt-2 mt-md-0"
                >
                  <li
                    class="page-item prev"
                    :class="{ disabled: currentPage === 1 }"
                  >
                    <a
                      class="page-link"
                      href="javascript:void(0)"
                      tabindex="-1"
                      @click="goToPage(currentPage - 1)"
                      ><i class="fas fa-angle-left"></i
                    ></a>
                  </li>
                  <li
                    class="page-item"
                    :class="{ active: currentPage === page }"
                    v-for="page in visiblePages"
                    :key="page"
                  >
                    <a
                      class="page-link"
                      href="javascript:void(0)"
                      @click="goToPage(page)"
                      >{{ page }}</a
                    >
                  </li>
                  <li
                    class="page-item next"
                    :class="{ disabled: currentPage === totalPages }"
                  >
                    <a
                      class="page-link"
                      href="javascript:void(0)"
                      @click="goToPage(currentPage + 1)"
                      ><i class="fas fa-angle-right"></i
                    ></a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
